import { Phone, MessageCircle, Navigation, CalendarCheck, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { track, whatsappLink } from "@/lib/analytics";

export default function Contact({ settings }) {
  const s = settings || {};
  const waHref = s.whatsapp_number ? whatsappLink(s.whatsapp_number, "Hi! I'd like to enquire about Mystique Copper Villa, Karwar.") : null;
  const cards = [
    s.phone && { icon: Phone, title: "Call Us", text: s.phone, href: `tel:${s.phone}`, event: "phone_click", testid: "contact-call-btn", external: false },
    waHref && { icon: MessageCircle, title: "WhatsApp", text: "Fastest reply — usually within the hour", href: waHref, event: "whatsapp_click", testid: "contact-whatsapp-btn", external: true },
    (s.maps_directions_url || s.maps_embed_url) && {
      icon: Navigation, title: "Directions", text: s.address || "Karwar, Karnataka",
      href: s.maps_directions_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address || "Karwar, Karnataka")}`,
      event: "directions_click", testid: "contact-directions-btn", external: true,
    },
  ].filter(Boolean);

  return (
    <section id="contact" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-20 sm:px-8 lg:px-16 lg:py-32" data-testid="contact-section">
      <Reveal>
        <p className="eyebrow mb-4">Find Us</p>
        <h2 className="max-w-3xl font-serif text-2xl leading-snug tracking-tight text-cream sm:text-3xl lg:text-4xl">
          The tide is waiting. <span className="italic text-copper-gradient">So are we.</span>
        </h2>
        {s.address && <p className="mt-4 text-base font-light text-cream-muted">{s.address}</p>}
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map(({ icon: Icon, title, text, href, event, testid, external }) => (
          <Reveal key={title}>
            <a href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined}
              onClick={() => track(event, { source: "contact" })} data-testid={testid}
              className="card-hover group flex h-full flex-col rounded-2xl border border-white/10 bg-charcoal-800 p-6">
              <Icon className="h-6 w-6 text-copper-400 transition-transform duration-500 group-hover:-translate-y-1" />
              <h3 className="mt-5 font-serif text-xl text-cream">{title}</h3>
              <p className="mt-2 text-sm font-light text-cream-muted">{text}</p>
            </a>
          </Reveal>
        ))}
        <Reveal>
          <a href="/#availability-widget"
            onClick={(e) => { e.preventDefault(); track("booking_cta_click", { source: "contact" }); document.getElementById("availability-widget")?.scrollIntoView({ behavior: "smooth", block: "center" }); }}
            data-testid="contact-check-availability-btn"
            className="group flex h-full flex-col rounded-2xl border border-copper/40 bg-copper/10 p-6 transition-all duration-500 hover:-translate-y-1.5 hover:bg-copper/20">
            <CalendarCheck className="h-6 w-6 text-copper-300 transition-transform duration-500 group-hover:-translate-y-1" />
            <h3 className="mt-5 font-serif text-xl text-cream">Check Availability</h3>
            <p className="mt-2 text-sm font-light text-cream-muted">Live dates &amp; secure payment on our booking engine</p>
          </a>
        </Reveal>
      </div>
    </section>
  );
}

export function Footer({ settings }) {
  const s = settings || {};
  return (
    <footer className="border-t border-white/10 px-4 pb-24 pt-14 sm:px-8 lg:px-16 lg:pb-14" data-testid="site-footer">
      <div className="mx-auto flex max-w-7xl flex-col gap-10 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="flex items-center gap-3 font-serif text-2xl text-cream"><img src="/logo.png" alt="Mystique Copper Villa emblem" className="h-12 w-12 rounded-full border border-copper/40 object-cover" /> Mystique <span className="text-copper-gradient italic">Copper</span> Villa</p>
          <p className="mt-3 max-w-sm text-sm font-light text-cream-muted">A boutique coastal homestay in Karwar, Karnataka. Bookings, availability and payments are handled securely on our booking engine.</p>
          {s.instagram_url && (
            <a href={s.instagram_url} target="_blank" rel="noreferrer" data-testid="footer-instagram-link" className="mt-4 inline-flex items-center gap-2 text-sm text-cream-muted transition-colors hover:text-copper-300">
              <Instagram className="h-4 w-4" /> Instagram
            </a>
          )}
        </div>
        <nav className="grid grid-cols-2 gap-x-16 gap-y-3 text-sm font-light text-cream-muted" aria-label="Footer">
          <a href="/#villa" className="transition-colors hover:text-copper-300">The Villa</a>
          <a href="/#rooms" className="transition-colors hover:text-copper-300">Stay</a>
          <a href="/#gallery" className="transition-colors hover:text-copper-300">Gallery</a>
          <a href="/#karwar" className="transition-colors hover:text-copper-300">Karwar</a>
          <a href="/#faq" className="transition-colors hover:text-copper-300">FAQ</a>
          <Link to="/blog" className="transition-colors hover:text-copper-300">Journal</Link>
        </nav>
        <div className="text-xs text-cream-dim">
          <p>© {new Date().getFullYear()} Mystique Copper Villa Homestay</p>
          <p className="mt-1">Karwar, Uttara Kannada, Karnataka</p>
          <Link to="/admin/login" className="mt-3 inline-block text-cream-dim/60 transition-colors hover:text-copper-300" data-testid="footer-admin-link">Owner login</Link>
        </div>
      </div>
    </footer>
  );
}
